import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable, toJS } from 'mobx';
import { observer } from 'mobx-react';
import * as Axios from 'axios';

import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { SearchProductsView } from './search-products.view';
import { ProductProviderDataService } from 'src/app/products/product-providers/product-provider.data';
import { CalculatePriceService } from 'src/app/bookings/shared/calculate-prices.service';
import { ProductCategoryDataService } from 'src/app/products/product-categories/form/product-category-form.data';

interface Props {
	cancelSearch: (source: Axios.CancelTokenSource) => void;
	setBusy(isBusy: boolean);
	marketId: number;
	bookingDate: Date;
	languageCode: string;
	paxs: PersonOccupancy[];
	onProductSelected: (selection: ResultSelection[], search: Search) => void;
	onSearchSelected: () => void;
	onSearch: (search: Search) => void;
	searchProducts: boolean;
	onOpen: () => void;
	showSearchText?: boolean;
	taxEditor?: React.ReactNode;
	disableAddButton?: (val: boolean) => void;
}

interface State {
	fieldsTaxonomy: TaxonomicField[];
}

@observer
export class SearchProductsController extends React.Component<Props, State> {
	@resolve
	private calculatePriceService: CalculatePriceService;

	@resolve
	private toast: ToastNotifications;

	@observable
	private dataService: ProductProviderDataService;

	@observable
	private searchPreferences: SearchPreferences = {
		pageIndex: 0,
		pageSize: 25,
		criteria: 'Price',
		direction: 'Asc',
	};

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			fieldsTaxonomy: []
		};
	}

	private _isMounted: boolean;

	public componentWillUnmount() {
		this._isMounted = false;
	}

	public componentDidMount(): void {
		this._isMounted = true;
		this.loadData()
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then(() => {
				this.props.setBusy(false);
			});
	}

	private async loadData(): Promise<void> {
		this.dataService = ProductProviderDataService.make();

		this.props.setBusy(true);
		this.dataService.addFilter('isDeleted', 'eq', false);
		this.dataService.addSorting('id', 'asc', true);

		await this.dataService.load(query => query);
	}

	filterTaxonomies = async (productTypeText: 'Accommodation' | 'CarRent' | 'Transfer' | 'Flights' | 'General', productCategoryId?: number): Promise<TaxonomicField[]> => {
		const productCategoriesDataService = ProductCategoryDataService.make();
		let productTypes: number[] = [];
		if (productTypeText === 'General') {
			productTypes = [0];
		} else if (productTypeText === 'Accommodation') {
			productTypes = [1, 5];
		} else if (productTypeText === 'CarRent') {
			productTypes = [2];
		} else if (productTypeText === 'Transfer') {
			productTypes = [3];
		} else if (productTypeText === 'Flights') {
			productTypes = [4];
		}

		const items = await productCategoriesDataService.getTaxonomiesForProduct(productTypes, productCategoryId);
		if (this._isMounted) {
			this.setState({
				fieldsTaxonomy: items
			});
		}
		return items;
	};

	searchAccommodation = async (item: SearchAccommodation): Promise<ApiResult> => {
		try {
			const source = Axios.default.CancelToken.source();
			this.props.cancelSearch(source);
			const result = await this.calculatePriceService.searchAccommodation(
				item,
				this.props.marketId,
				this.props.bookingDate,
				this.props.languageCode,
				toJS(this.searchPreferences),
				{ cancelToken: source.token },
			);
			this.props.cancelSearch(undefined);
			return result;
		} catch (error) {
			throw error;
		}
	}

	searchCarRent = async (item: SearchCarRent): Promise<ApiResult> => {
		try {
			const source = Axios.default.CancelToken.source();
			this.props.cancelSearch(source);
			const result = await this.calculatePriceService.searchCarRent(
				item,
				this.props.marketId,
				this.props.bookingDate,
				this.props.languageCode,
				toJS(this.searchPreferences),
				{ cancelToken: source.token },
			);
			this.props.cancelSearch(undefined);
			return result;
		} catch (error) {
			throw error;
		}
	}

	searchGeneral = async (item: SearchGeneral): Promise<ApiResult> => {
		try {
			const source = Axios.default.CancelToken.source();
			this.props.cancelSearch(source);
			const result = await  this.calculatePriceService.searchGeneral(
				item,
				this.props.marketId,
				this.props.bookingDate,
				this.props.languageCode,
				toJS(this.searchPreferences),
				{ cancelToken: source.token },
			);
			this.props.cancelSearch(undefined);
			return result;
		} catch (error) {
			throw error;
		}
	}

	searchTour = async (item: SearchTour): Promise<ApiResult> => {
		try {
			const source = Axios.default.CancelToken.source();
			this.props.cancelSearch(source);
			const result = await  this.calculatePriceService.searchTour(
				item,
				this.props.marketId,
				this.props.bookingDate,
				this.props.languageCode,
				toJS(this.searchPreferences),
				{ cancelToken: source.token },
			);
			this.props.cancelSearch(undefined);
			return result;
		} catch (error) {
			throw error;
		}
	}

	searchTransfer = async (item: SearchTransfer): Promise<ApiResult> => {
		const messages = [];
		if (item.fromPlaceId == null) {
			messages.push("Pick up place is required!");
		}
		if (item.toPlaceId == null) {
			messages.push("Drop off place is required!");
		}
		if (messages.length > 0) {
			this.toast.showToast("Errors in search parameters", messages, 'error');
			this.props.onSearchSelected();
			return;
		}
		try {
			const source = Axios.default.CancelToken.source();
			this.props.cancelSearch(source);
			const result = await  this.calculatePriceService.searchTransfer(
				item,
				this.props.marketId,
				this.props.bookingDate,
				this.props.languageCode,
				toJS(this.searchPreferences),
				{ cancelToken: source.token },
			);
			this.props.cancelSearch(undefined);
			return result;
		} catch (error) {
			throw error;
		}
	}

	searchFlight = async (item: SearchFlight): Promise<ApiResult> => {
		try {
			const source = Axios.default.CancelToken.source();
			this.props.cancelSearch(source);
			const result = await  this.calculatePriceService.searchFlight(
				item,
				this.props.marketId,
				this.props.bookingDate,
				this.props.languageCode,
				toJS(this.searchPreferences),
				{ cancelToken: source.token },
			);
			this.props.cancelSearch(undefined);
			return result;
		} catch (error) {
			throw error;
		}
	}

	render(): React.ReactNode {
		if (this.dataService == null || this.dataService.items.length == 0) {
			return null;
		}
		const { fieldsTaxonomy } = this.state;

		return (
			<SearchProductsView
				ppDataService={this.dataService}
				searchAccommodation={this.searchAccommodation}
				searchGeneral={this.searchGeneral}
				searchTransfer={this.searchTransfer}
				searchFlight={this.searchFlight}
				searchCarRent={this.searchCarRent}
				searchTour={this.searchTour}
				paxs={this.props.paxs}
				occupancy={[{
					unit: 1,
					paxs: this.props.paxs.slice()
				}]}
				searchPreferences={this.searchPreferences}
				onOpen={this.props.onOpen}
				setBusy={this.props.setBusy}
				onProductSelected={this.props.onProductSelected}
				onSearch={this.props.onSearch}
				searchProducts={this.props.searchProducts}
				onSearchSelected={this.props.onSearchSelected}
				bookingDate={this.props.bookingDate}
				languageCode={this.props.languageCode}
				marketId={this.props.marketId}
				showSearchText={this.props.showSearchText}
				fieldsTaxonomy={fieldsTaxonomy}
				filterTaxonomies={this.filterTaxonomies}
				taxEditor={this.props.taxEditor}
				disableAddButton={this.props.disableAddButton}
			>
				{this.props.children}
			</SearchProductsView>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/search/search-products.controller.tsx